import { z } from 'zod';
import { FormatConfigSchema, HandicapConfigSchema } from './scoring';

/** ScoreAuditLog.deviceInfo / ContestEntry.deviceInfo — sent by the client with every op batch. */
export const DeviceInfoSchema = z.object({
  deviceId: z.string().max(64),
  platform: z.string().max(64).optional(),
  userAgent: z.string().max(400).optional(),
  appVersion: z.string().max(32).optional(),
  screen: z.string().max(32).optional(),
  online: z.boolean().optional(),
  standalone: z.boolean().optional(), // launched as installed PWA
});
export type DeviceInfo = z.infer<typeof DeviceInfoSchema>;

/**
 * RoundCourse.courseSnapshot — the course frozen at round start. Scoring, leaderboard, handicap
 * and exports read ONLY this once set (course edits never change started/locked rounds).
 */
export const RoundCourseSnapshotSchema = z.object({
  courseId: z.uuid(),
  name: z.string(),
  holeCount: z.int(),
  holes: z.array(
    z.object({
      number: z.int(),
      par: z.int(),
      strokeIndex: z.int(),
      parWomen: z.int().nullable(),
      strokeIndexWomen: z.int().nullable(),
    }),
  ),
  tees: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      color: z.string().nullable(),
      ratings: z.array(
        z.object({
          gender: z.enum(['M', 'F']),
          courseRating: z.number(),
          slopeRating: z.int(),
          par: z.int(),
          frontRating: z.number().nullable(),
          frontSlope: z.int().nullable(),
          backRating: z.number().nullable(),
          backSlope: z.int().nullable(),
        }),
      ),
      /** hole number -> meters */
      distances: z.record(z.string(), z.number().nullable()),
    }),
  ),
  snapshotAt: z.iso.datetime(),
});
export type RoundCourseSnapshot = z.infer<typeof RoundCourseSnapshotSchema>;

/** Club.settings */
export const ClubSettingsSchema = z.object({
  defaultFormat: FormatConfigSchema.default({ kind: 'STROKE_GROSS' }),
  defaultHandicap: HandicapConfigSchema.optional(),
  allowSelfEntry: z.boolean().default(true),
  memberCanCreateEvents: z.boolean().default(false),
  defaultCourseId: z.uuid().nullable().default(null),
});
export type ClubSettings = z.infer<typeof ClubSettingsSchema>;

/** Season.config */
export const SeasonConfigSchema = z.object({
  pointsTable: z
    .object({
      byRank: z
        .array(z.number())
        .default([100, 80, 65, 55, 50, 45, 40, 36, 32, 29, 26, 24, 22, 20, 18, 16, 14, 12, 10, 8]),
      participation: z.number().default(5),
      ties: z.enum(['AVERAGE', 'SHARED_POSITION']).default('AVERAGE'),
    })
    .default({
      byRank: [100, 80, 65, 55, 50, 45, 40, 36, 32, 29, 26, 24, 22, 20, 18, 16, 14, 12, 10, 8],
      participation: 5,
      ties: 'AVERAGE',
    }),
  /** count only best N events (null = all) */
  bestN: z.int().min(1).nullable().default(null),
  /** which leaderboard view decides event positions */
  view: z.enum(['STROKE_GROSS', 'STROKE_NET', 'STABLEFORD']).default('STROKE_NET'),
});
export type SeasonConfig = z.infer<typeof SeasonConfigSchema>;
